import React, { useCallback, useEffect, useState } from 'react';
import { Delete, StarTwoTone } from '@mui/icons-material';
import {
  Button, Divider, Drawer, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth, useRequest } from 'shared/hooks';
import { InsetNavLink, SideNavLink } from 'shared/components/NavLink';
import {
  clearRecentHistory,
  deleteRecentHistoryItem,
  favoriteHistoryItem,
  listHistory,
  processHistoryResult,
  pushRecentHistoryItem,
  unfavoriteHistoryItem,
} from 'shared/api/user_profile';
import { TooltipIcon } from 'shared/components/abstract/interactables';
import EdgeSwipeDrawer from 'shared/components/abstract/drawers';

export const SideNavContext = React.createContext(() => null);

function withSideNav(WrappedComponent, title, OptionalComponents = undefined) {
  // A navbar on the side of the page that renders with a component.
  // E.G. In Coupon component, there are links to 'create', and 'view'.
  // There is a lower section that displays a history of items that the user viewed or created.
  //
  // Child components may subscribe to the SideNavContext to control the displayed links.
  /**
   * @param {Object} props
   * @param {Array<Object>} [props.links]
   * @param {string} props.links[].path
   * @param {string} props.links[].text
   * @param {string} [props.links[].group]
   * @param {string} [props.links[].groupHead]
   */
  function SideNav(props) {
    const {
      links,
      ...passThrough
    } = props;

    const { request } = useRequest();
    const { user: { id: userId } } = useAuth();

    const [favoriteMode, setFavoriteMode] = useState(false);
    const [history, setState] = useState(undefined);

    const setHistory = (raw) => setState(processHistoryResult(raw));

    // Fetch the existing history when this component mounts.
    useEffect(() => {
      (async () => {
        if (!userId) return;
        const result = await request(listHistory(userId, title));
        if (result) {
          setHistory(result);
        }
      })();
    }, [userId, request]);

    // Syncs the side nav with path and text. This will delete and/or create an item.
    // This uses a callback function so that the value of syncSideNav doesn't change every render.
    // This reduces the number of times that dependent effects run.
    const syncSideNav = useCallback(async (path, text) => {
      if (!history) return; // Ensure that history has been fetched before syncing.
      let result;
      const newItem = { path, text };
      const currentItem = history[history.findIndex((curr) => curr.path === path)];

      // Update or delete the current item.
      if (currentItem && currentItem.text !== newItem.text) {
        if (currentItem.favorited) {
          result = await request(unfavoriteHistoryItem(userId, title, currentItem));
        } else {
          result = await request(deleteRecentHistoryItem(userId, title, currentItem));
        }
        if (newItem.text) {
          if (currentItem.favorited) {
            result = await request(favoriteHistoryItem(userId, title, newItem));
          } else {
            result = await request(pushRecentHistoryItem(userId, title, newItem));
          }
        }
        if (result) {
          setHistory(result);
        }
        return;
      }

      // Push a new recent item, or move a recent item to the top of the stack.
      if (!currentItem || (!currentItem.favorited && currentItem.position > 0)) {
        result = await request(pushRecentHistoryItem(userId, title, newItem));
        if (result) {
          setHistory(result);
        }
      }
    }, [userId, request, history]);

    const toggleFavoriteMode = () => {
      setFavoriteMode(!favoriteMode);
    };

    const toggleFavorite = async (item) => {
      let result;
      if (item.favorited) {
        result = await request(unfavoriteHistoryItem(userId, title, item));
      } else {
        result = await request(favoriteHistoryItem(userId, title, item));
      }

      if (result) {
        setHistory(result);
      }
    };

    const deleteAll = async () => {
      const result = await request(clearRecentHistory(userId, title));
      if (result) {
        setHistory(result);
      }
    };

    const drawer = (
      <Grid
        container
        sx={{
          height: '100%',
          paddingLeft: (theme) => theme.spacing(3),
          paddingRight: (theme) => theme.spacing(1),
          // Room for the focus ring of the first and last links; this box scrolls.
          paddingTop: '4px',
          paddingBottom: '8px',
          overflow: 'auto',
        }}
      >
        <Grid container sx={{ alignContent: 'start' }}>
          {links && links.length > 0 && (
            <>
              {links.map((r) => (
                <SideNavLink
                  key={r.text}
                  text={r.text}
                  path={r.path}
                  group={r.group}
                  groupHead={r.groupHead}
                />
              ))}
              {((history && history.length > 0) || OptionalComponents) && (
              <Grid size={12} sx={{ marginTop: (theme) => theme.spacing(2) }}>
                <Divider sx={{ marginRight: (theme) => theme.spacing(2) }} />
              </Grid>
              )}
            </>
          )}
          {history && history.length > 0
          && (
            <Grid sx={{ marginTop: (theme) => theme.spacing(2) }}>
              <Typography variant="h6" className="side-nav-title">{`${title} History`}</Typography>
                {favoriteMode
                  ? (
                    <Grid container spacing={1} size={11}>
                      <Typography variant="subtitle2">ADD/REMOVE FAVORITES</Typography>
                      {history.map((item) => (
                        item.text !== null && item.path !== null
                        && (
                          <Grid size={12}>
                            <Button
                              key={item.path}
                              onClick={() => toggleFavorite(item)}
                              fullWidth
                              variant="outlined"
                              sx={{ padding: '5px 5px 0' }}
                            >
                              <Grid container size={12} sx={{ alignItems: 'center' }}>
                                {item.favorited && (
                                <Grid size={2}>
                                  <StarTwoTone className="star-selected" />
                                </Grid>
                                )}
                                <Grid size={9}>{item.text}</Grid>
                              </Grid>
                            </Button>
                          </Grid>
                        )
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={1} size={12}>
                      {history.map((item) => (
                        item.text !== null && item.path !== null
                      && (
                        <Grid size={12} key={item.path}>
                          <InsetNavLink
                            favorited={item.favorited}
                            text={item.text}
                            path={item.path}
                          />
                        </Grid>
                      )
                      ))}
                    </Grid>
                  )}

            </Grid>
          )}
          {OptionalComponents && (
          <OptionalComponents />
          )}
        </Grid>
        {!OptionalComponents && (
        <Grid
          container
          size={12}
          sx={{
            alignSelf: 'end',
            justifyContent: 'space-between',
            paddingBottom: (theme) => theme.spacing(2),
            paddingRight: (theme) => theme.spacing(2),
            marginTop: (theme) => theme.spacing(1),
          }}
        >
          <Grid>
            <TooltipIcon
              className={`star ${favoriteMode ? 'star-selected' : ''}`}
              onClick={toggleFavoriteMode}
              icon={StarTwoTone}
              message="Add / remove favorites"
            />
          </Grid>
          <Grid>
            <TooltipIcon
              className="trash"
              icon={Delete}
              onClick={deleteAll}
              message="Remove all unfavorited items"
              placement="right"
            />
          </Grid>
        </Grid>
        )}
      </Grid>
    );

    const drawerWidth = 250;

    return (
      <>
        <EdgeSwipeDrawer content={drawer} />
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            maxWidth: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'sideNavBackground',
            },
          }}
          open
          anchor="left"
        >
          <Grid
            sx={{ height: '100%', marginTop: (theme) => theme.spacing(12.5) }}
          >
            {drawer}
          </Grid>
        </Drawer>
        <SideNavContext.Provider value={syncSideNav}>
          <WrappedComponent {...passThrough} />
        </SideNavContext.Provider>
      </>
    );
  }

  return SideNav;
}

export function connectSideNav(title) {
  return (component) => withSideNav(component, title);
}

export function connectSideNavWithOptionalComponents({ optionalComponents, title }) {
  return (component) => withSideNav(component, title, optionalComponents);
}
